/* istanbul ignore file - tested e2e */

import { cadExportInstructions } from '@constants/URLs';
import {
  faCheckSquare,
  faCloudExclamation,
  faRight,
  faSquare,
  faWandMagicSparkles,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { Button } from '@partful/shared/ui/Button';
import DataGrid from '@partful/shared/ui/DataGrid';
import {
  WizardPageContent,
  WizardPageFooter,
  WizardPageHeader,
  WizardPageLayout,
} from '@partful/shared/ui/layouts/WizardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { getCADJob, patchCADJob, PATCHCADJobResponse } from '@services/productInventory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import styles from './GeneralAssemblyPage.module.scss';

export function GeneralAssemblyPage() {
  const { t: i18n } = useTranslation();
  const location = useLocation();
  const { accountId } = useTenant();
  const searchParams = new URLSearchParams(location?.search);
  const runID = searchParams.get('runID');
  const productName = searchParams.get('productName');

  const {
    isLoading,
    isError,
    data: files,
  } = useQuery(['getCadRunInfo', runID], ({ queryKey: [_name, runID] }) => {
    if (!runID) {
      throw new Error('Missing run ID');
    }
    return getCADJob(runID, accountId);
  });

  const {
    mutate,
    isError: isErrorPatch,
    isLoading: isLoadingPatch,
    isSuccess: patchingComplete,
  } = useMutation<PATCHCADJobResponse, unknown, { path: string; runId?: string | null }>(
    ['patchCadRunInfo'],
    ({ path, runId }) => {
      if (runId == null) {
        throw new Error('Missing run ID');
      }

      return patchCADJob(runId, accountId, { selectedGeneralAssembly: path });
    }
  );

  const [rowSelection, setRowSelection] = useState<GridRowSelectionModel>([]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const selectedRow = initialRows?.find((row) => row.id === rowSelection[0]);

    if (selectedRow) {
      mutate({
        path: selectedRow.fileName,
        runId: runID,
      });
    }
  };

  if (patchingComplete) {
    return <Redirect to={`/instructions/create/processing?runID=${runID}&productName=${productName}`} />;
  }

  const initialRows = files?.fileDetails
    .sort((a, b) => b.size - a.size)
    .map((file, index) => ({
      id: index + 1,
      fileName: file.path,
      size: file.size,
    }));

  const columns: GridColDef[] = [
    { field: 'id' },
    {
      field: 'fileName',
      flex: 1,
      headerAlign: 'left',
      renderHeader: () => (
        <div>
          <Typography appearance="heading6" as="p" fontWeight={600}>
            {i18n('instructions:create.generalAssembly.assemblyFiles')}
          </Typography>
          <Typography appearance="label" as="p">
            {i18n('instructions:create.generalAssembly.partFilesNotice')}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <span title={params.value} className={styles.cell}>
          <FontAwesomeIcon icon={rowSelection.includes(params.row.id) ? faCheckSquare : faSquare} />
          {params.value}
        </span>
      ),
    },
    {
      field: 'size',
      width: 150,
      valueFormatter: (value) => `${(value / 1000000).toFixed(2)}Mb`,
      renderHeader: () => <Typography fontWeight={600}>{i18n('instructions:create.generalAssembly.size')}</Typography>,
    },
  ];

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isErrorPatch || isError || !runID) {
    return (
      <WizardErrorPage
        pageTitle={
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
          </>
        }
        pageSubtitle={i18n('instructions:create.generalAssembly.title')}
        errorIcon={<FontAwesomeIcon icon={faCloudExclamation} />}
        errorTitle={i18n('instructions:create.generalAssembly.filesUploadFailed', 'the uploaded CAD file')}
        errorMessage={
          <Trans i18nKey="instructions:create.CAD.fileValidationFailed.message">
            <Typography as="a" href={cadExportInstructions} target="_blank" rel="noreferrer" />
          </Trans>
        }
        errorActions={null}
      />
    );
  }

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <Typography appearance="heading5" as="h1">
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
        </Typography>
        <Typography appearance="heading6" as="h2" data-testid="assembly-title">
          {i18n('instructions:create.generalAssembly.title')}
        </Typography>
        <Typography>
          <Trans>{i18n('instructions:create.generalAssembly.message')}</Trans>
        </Typography>
      </WizardPageHeader>
      <WizardPageContent className={styles.pageContent}>
        <DataGrid
          className={styles.dataGrid}
          data-testid="data-grid"
          columns={columns}
          rows={initialRows}
          columnHeaderHeight={66}
          rowHeight={40}
          rowSelectionModel={rowSelection}
          onRowSelectionModelChange={(newSelection) => setRowSelection(newSelection)}
          columnVisibilityModel={{
            id: false,
          }}
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          hideFooter
        />
      </WizardPageContent>
      <WizardPageFooter>
        <Typography>{i18n('instructions:common.allChangesSaved')}</Typography>
        <Button
          size="large"
          disabled={rowSelection.length === 0 || isLoadingPatch}
          onClick={handleSubmit}
          data-testid="submit-button"
        >
          {i18n('instructions:common.nextStep')}
          <FontAwesomeIcon icon={faRight} />
        </Button>
      </WizardPageFooter>
    </WizardPageLayout>
  );
}
